import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { FormikErrors, useFormik } from 'formik';

import { convertRequestErrorToMap, useUpdateEffect } from '@tager/web-core';

import { colors } from '@/constants/theme';
import { media } from '@/utils/mixin';
import { StyledAlfaLeasingLogo } from '@/components/modals/AlfaLeasingFormModal/AlfaLeasingFormModal.styles';
import Loader from '@/components/Loader';
import { CheckboxAgreement } from '@/components/Checkbox';
import StaticTextInput from '@/components/StaticTextInput';
import SuccessForm from '@/components/Forms/SuccessForm';
import CloseButton from '@/components/modals/shared/CloseButton';
import Button from '@/components/Button';
import Select from '@/components/Select';
import { OptionType } from '@/typings/common';

import {
  approvedLeasingText,
  approvedLeasingTitle,
  initialAlfaLeasingFormValues,
  notApprovedLeasingText,
  notApprovedLeasingTitle,
  validationAlfaLeasingFormSchema,
} from './AlfaLeasingForm.helpers';
import { AlfaLeasingFormFields, AlfaLeasingFormProps } from './types';

function AlfaLeasingForm({
  onClose,
  onSubmitAlfaLeasingForm,
  dealers,
  isApproved,
}: AlfaLeasingFormProps) {
  const [userAgreement, setUserAgreement] = useState(false);
  const [isFormSend, setFormSend] = useState(false);
  const [dealer, setDealer] = useState<OptionType>({
    label: '',
    value: '',
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    setFieldValue,
    setErrors,
    isSubmitting,
  } = useFormik<AlfaLeasingFormFields>({
    initialValues: initialAlfaLeasingFormValues,
    validationSchema: validationAlfaLeasingFormSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);

      try {
        await onSubmitAlfaLeasingForm?.(values);
        setFormSend(true);
      } catch (error: any) {
        console.log(error);
        const errorMap = convertRequestErrorToMap(
          error
        ) as FormikErrors<AlfaLeasingFormFields>;
        setErrors(errorMap);
      }

      setSubmitting(false);
    },
  });

  useUpdateEffect(() => {
    setFieldValue('dealer', dealer);
  }, [dealer]);

  return (
    <Component
      isFormSend={isFormSend}
      onSubmit={handleSubmit}
      noValidate
      autoComplete="off"
    >
      <CloseButton onClick={onClose} />

      {!isFormSend ? (
        <React.Fragment>
          <StyledAlfaLeasingLogo />
          <Left>
            <Title>
              {isApproved ? approvedLeasingTitle : notApprovedLeasingTitle}
            </Title>
            <Text>
              {isApproved ? approvedLeasingText : notApprovedLeasingText}
            </Text>
          </Left>
          <Right>
            <FormContent>
              <FieldRow>
                <StaticTextInput
                  id="name"
                  name="name"
                  placeholder="Имя"
                  value={values.name}
                  onChange={handleChange}
                  errorMessage={
                    errors.name && touched.name ? errors.name : undefined
                  }
                  isWhiteBackground
                />
              </FieldRow>

              <FieldRow>
                <StaticTextInput
                  id="phone"
                  name="phone"
                  placeholder="Телефон"
                  value={values.phone}
                  onChange={handleChange}
                  errorMessage={
                    errors.phone && touched.phone ? errors.phone : undefined
                  }
                  isWhiteBackground
                />
              </FieldRow>

              <FieldRow>
                <Select
                  label="Автоцентр"
                  value={dealer}
                  options={dealers}
                  onChange={setDealer}
                  errorMessage={
                    errors.dealer?.value && touched.dealer?.value
                      ? errors.dealer.value
                      : undefined
                  }
                />
              </FieldRow>

              <Bottom>
                <ButtonWrapper>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={!isSubmitting ? !userAgreement : true}
                  >
                    {!isSubmitting ? (
                      'Отправить'
                    ) : (
                      <Loader color={colors.white} />
                    )}
                  </Button>
                </ButtonWrapper>
                <CheckBoxWrapper>
                  <CheckboxAgreement
                    checked={userAgreement}
                    onChange={() => setUserAgreement(!userAgreement)}
                  />
                </CheckBoxWrapper>
              </Bottom>
            </FormContent>
          </Right>
        </React.Fragment>
      ) : (
        <SuccessForm />
      )}
    </Component>
  );
}

export default AlfaLeasingForm;

const Component = styled.form<{ isFormSend: boolean }>`
  position: relative;
  display: flex;
  width: calc(100vw - 20px);
  max-width: 738px;

  ${media.mobile(css`
    flex-direction: column;
    max-width: none;
  `)}

  ${({ isFormSend }) =>
    isFormSend &&
    css`
      border-radius: 10px;
      justify-content: center;
      padding: 32px 40px 48px;
      background: ${(props) => props.theme.white};
    `}
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 50%;
  padding: 28px 40px;
  border-radius: 10px 0 0 10px;
  background: ${colors.main};

  ${media.mobile(css`
    padding: 25px 10px;
    border-radius: 10px 10px 0 0;
  `)}
`;

const Title = styled.p`
  font-weight: 700;
  font-size: 26px;
  line-height: 30px;
  color: ${colors.white};

  ${media.mobile(css`
    line-height: 28px;
    margin: 0 auto;
    padding: 0 40px;
    text-align: center;
  `)}
`;

const Text = styled.p`
  font-size: 18px;
  line-height: 21px;
  margin-top: 25px;
  color: ${colors.white};

  ${media.mobile(css`
    text-align: center;
  `)}
`;

const Right = styled.div`
  flex: 50%;
  padding: 28px 40px;
  border-radius: 0 10px 10px 0;
  background: ${(props) => props.theme.white};

  ${media.mobile(css`
    padding: 25px 10px;
    border-radius: 0 0 10px 10px;
  `)}
`;

const FormContent = styled.div`
  margin-top: 69px;

  ${media.mobile(css`
    margin-top: 0;
  `)}
`;

const FieldRow = styled.div`
  &:not(:first-child) {
    margin-top: 20px;
  }
`;

const Bottom = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 25px;

  ${media.mobile(css`
    flex-direction: column-reverse;
  `)}
`;

const ButtonWrapper = styled.div`
  ${media.mobile(css`
    margin-top: 25px;
  `)}
`;

const CheckBoxWrapper = styled.div`
  margin-top: 10px;

  ${media.mobile(css`
    margin-top: 0;
  `)}
`;
